/**
 * Style Clone Service - Phase 2.3: Style Cloning Implementation
 *
 * Enables users to copy styles from one component to another using natural language:
 * - "Copy the style from the header"
 * - "Match the button's colors"
 * - "Use the same font as the title"
 * - "Make this look like the card above"
 *
 * Integrates with ComponentReferenceResolver for semantic reference resolution.
 */

import { Component, ComponentType } from '@/types';
import { componentReferenceResolver } from './ComponentReferenceResolver';
import { useEditorStore } from '@/stores/editor/editor-store';
import type { PropertyChange } from './commands/BaseCommandHandler';

// ============================================================================
// Types and Interfaces
// ============================================================================

/**
 * Scope of styles to clone
 */
export type StyleCloneScope =
  | 'all'        // Clone all applicable styles
  | 'colors'     // Clone color-related styles only
  | 'typography' // Clone font-related styles only
  | 'spacing'    // Clone padding, margin, gap only
  | 'borders'    // Clone border-related styles only
  | 'shadows'    // Clone shadow-related styles only
  | 'layout';    // Clone layout-related styles only

/**
 * Request to clone styles from a source component
 */
export interface StyleCloneRequest {
  /** Reference text for source component (e.g., "the header", "submit button") */
  sourceRef: string;
  /** Target component to apply styles to */
  targetComponent: Component;
  /** Scope of properties to clone */
  propertyScope: StyleCloneScope;
  /** Optional context for resolution */
  context?: {
    selectedComponents?: Component[];
    templateInstanceId?: string;
    pageId?: string;
  };
}

/**
 * Result of style cloning operation
 */
export interface StyleCloneResult {
  /** Whether cloning was successful */
  success: boolean;
  /** Cloned styles to apply */
  clonedStyles?: PropertyChange;
  /** Source component that was resolved */
  sourceComponent?: Component;
  /** Warnings about incompatible properties */
  warnings: string[];
  /** Error message if unsuccessful */
  error?: string;
  /** Properties that were skipped due to incompatibility */
  skippedProperties?: string[];
  /** Whether clarification is needed (multiple matches found) */
  needsClarification?: boolean;
  /** Matched components when clarification is needed */
  matchedComponents?: Array<{
    id: string;
    name: string;
    type: string;
    matchReason: string;
    confidence: number;
  }>;
}

/**
 * Extracted styles from a component
 */
export interface ExtractedStyles {
  style: Record<string, unknown>;
  properties: Record<string, unknown>;
  size?: { width?: number | string; height?: number | string };
}

// ============================================================================
// Style Scope Definitions
// ============================================================================

/**
 * Style properties organized by scope
 * These define which CSS/component properties belong to each category
 */
const STYLE_SCOPES: Record<StyleCloneScope, string[]> = {
  colors: [
    'color', 'backgroundColor', 'borderColor', 'fill', 'stroke',
    'outlineColor', 'caretColor', 'textDecorationColor',
    // Component-specific color properties
    'labelColor', 'valueColor', 'accentColor', 'iconColor'
  ],
  typography: [
    'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'letterSpacing',
    'textAlign', 'textTransform', 'textDecoration', 'fontStyle',
    'wordSpacing', 'whiteSpace', 'textOverflow'
  ],
  spacing: [
    'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
    'gap', 'rowGap', 'columnGap'
  ],
  borders: [
    'border', 'borderWidth', 'borderStyle', 'borderColor',
    'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
    'borderRadius', 'borderTopLeftRadius', 'borderTopRightRadius',
    'borderBottomLeftRadius', 'borderBottomRightRadius',
    'outline', 'outlineWidth', 'outlineStyle', 'outlineOffset'
  ],
  shadows: [
    'boxShadow', 'textShadow', 'dropShadow'
  ],
  layout: [
    'display', 'flexDirection', 'flexWrap', 'justifyContent', 'alignItems',
    'alignContent', 'alignSelf', 'flex', 'flexGrow', 'flexShrink', 'flexBasis',
    'gridTemplateColumns', 'gridTemplateRows', 'gridColumn', 'gridRow',
    'order', 'position', 'overflow', 'overflowX', 'overflowY'
  ],
  all: [] // Will include all properties from other scopes
};

// Build 'all' scope from other scopes
STYLE_SCOPES.all = [
  ...STYLE_SCOPES.colors,
  ...STYLE_SCOPES.typography,
  ...STYLE_SCOPES.spacing,
  ...STYLE_SCOPES.borders,
  ...STYLE_SCOPES.shadows,
  ...STYLE_SCOPES.layout
];

/**
 * Component-specific properties that may not apply to all types
 * Maps property names to applicable component types
 */
const COMPONENT_SPECIFIC_PROPERTIES: Record<string, ComponentType[]> = {
  // Text-only properties
  fontSize: [ComponentType.TEXT, ComponentType.BUTTON],
  fontWeight: [ComponentType.TEXT, ComponentType.BUTTON],
  fontFamily: [ComponentType.TEXT, ComponentType.BUTTON],
  lineHeight: [ComponentType.TEXT],
  letterSpacing: [ComponentType.TEXT],
  textAlign: [ComponentType.TEXT, ComponentType.BUTTON],
  textTransform: [ComponentType.TEXT],
  textDecoration: [ComponentType.TEXT],
  fontStyle: [ComponentType.TEXT],
  textShadow: [ComponentType.TEXT],

  // Button-specific
  variant: [ComponentType.BUTTON],

  // Container-specific
  flexDirection: [ComponentType.FLEX_CONTAINER, ComponentType.CONTAINER],
  justifyContent: [ComponentType.FLEX_CONTAINER, ComponentType.CONTAINER],
  alignItems: [ComponentType.FLEX_CONTAINER, ComponentType.CONTAINER],
  gap: [ComponentType.FLEX_CONTAINER, ComponentType.CONTAINER],
  flexWrap: [ComponentType.FLEX_CONTAINER, ComponentType.CONTAINER],
};

// ============================================================================
// Clone Pattern Definitions
// ============================================================================

/**
 * Patterns for detecting style clone commands
 * Returns: [regex, scope]
 */
export const CLONE_PATTERNS: Array<[RegExp, StyleCloneScope]> = [
  // "copy the style from X" / "copy X's style"
  [/copy\s*(?:the\s*)?style\s*(?:from|of)\s+(.+)/i, 'all'],
  [/copy\s+(.+?)(?:'s|s')\s*style/i, 'all'],

  // "match the X's colors" / "match colors from X" / "match the color with X"
  [/match\s*(?:the\s*)?colors?\s*(?:with|from|of)\s+(?:the\s*)?(.+)/i, 'colors'],
  [/match\s*(?:the\s*)?(.+?)(?:'s|s')\s*colors?/i, 'colors'],
  [/match\s*colors?\s*(?:from|of)\s+(.+)/i, 'colors'],

  // "use the same font as X" / "same typography as X"
  [/use\s*(?:the\s*)?same\s*(?:font|typography)\s*(?:as|from)\s+(.+)/i, 'typography'],
  [/match\s*(?:the\s*)?(.+?)(?:'s|s')?\s*(?:font|typography)/i, 'typography'],
  [/copy\s*(?:the\s*)?(?:font|typography)\s*(?:from|of)\s+(.+)/i, 'typography'],

  // "copy spacing from X" / "match X's spacing"
  [/copy\s*(?:the\s*)?spacing\s*(?:from|of)\s+(.+)/i, 'spacing'],
  [/match\s*(?:the\s*)?(.+?)(?:'s|s')?\s*spacing/i, 'spacing'],
  [/use\s*(?:the\s*)?same\s*spacing\s*(?:as|from)\s+(.+)/i, 'spacing'],

  // "copy borders from X" / "match X's border style"
  [/copy\s*(?:the\s*)?borders?\s*(?:from|of)\s+(.+)/i, 'borders'],
  [/match\s*(?:the\s*)?(.+?)(?:'s|s')?\s*borders?/i, 'borders'],

  // "copy shadow from X"
  [/copy\s*(?:the\s*)?shadows?\s*(?:from|of)\s+(.+)/i, 'shadows'],
  [/match\s*(?:the\s*)?(.+?)(?:'s|s')?\s*shadows?/i, 'shadows'],

  // "look like X" / "style like X" / "make this look like X"
  [/(?:make\s*(?:this|it)\s*)?look\s*like\s*(?:the\s*)?(.+)/i, 'all'],
  [/style\s*(?:this\s*)?like\s*(?:the\s*)?(.+)/i, 'all'],

  // "same style as X"
  [/(?:use\s*)?(?:the\s*)?same\s*style\s*(?:as|from)\s+(.+)/i, 'all'],

  // "copy layout from X"
  [/copy\s*(?:the\s*)?layout\s*(?:from|of)\s+(.+)/i, 'layout'],
  [/match\s*(?:the\s*)?(.+?)(?:'s|s')?\s*layout/i, 'layout'],
];

// ============================================================================
// Style Clone Service Class
// ============================================================================

export class StyleCloneService {
  private static instance: StyleCloneService;

  private constructor() {}

  static getInstance(): StyleCloneService {
    if (!StyleCloneService.instance) {
      StyleCloneService.instance = new StyleCloneService();
    }
    return StyleCloneService.instance;
  }

  // ============================================================================
  // Main Public Methods
  // ============================================================================

  /**
   * Check if a command is a style clone request
   * Returns the match info if it's a clone command, null otherwise
   */
  parseCloneCommand(command: string): { sourceRef: string; scope: StyleCloneScope } | null {
    const normalizedCommand = command.trim().toLowerCase();

    for (const [pattern, scope] of CLONE_PATTERNS) {
      const match = normalizedCommand.match(pattern);
      if (match && match[1]) {
        const sourceRef = match[1].trim();
        return { sourceRef, scope };
      }
    }

    return null;
  }

  /**
   * Clone styles from a source component to a target component
   */
  async cloneStyles(request: StyleCloneRequest): Promise<StyleCloneResult> {
    const { sourceRef, targetComponent, propertyScope, context } = request;

    // Validate sourceRef is not empty
    if (!sourceRef || sourceRef.trim() === '') {
      return {
        success: false,
        warnings: [],
        error: 'Could not determine which component to copy from. Please specify a component name.'
      };
    }

    // Step 1: Resolve source reference to component
    const resolveResult = this.resolveSourceComponent(sourceRef, context);
    if (!resolveResult.success || !resolveResult.component) {
      // If multiple matches found, return them for clarification
      if (resolveResult.needsClarification && resolveResult.matches) {
        return {
          success: false,
          warnings: [],
          error: resolveResult.error || `Multiple components match "${sourceRef}". Please select one.`,
          needsClarification: true,
          matchedComponents: resolveResult.matches
        };
      }

      return {
        success: false,
        warnings: [],
        error: resolveResult.error || `Could not find component matching "${sourceRef}"`
      };
    }

    const sourceComponent = resolveResult.component;

    // Step 2: Extract styles from source component
    const extractedStyles = this.extractStyles(sourceComponent, propertyScope);

    // Step 3: Filter for compatible properties
    const { compatible, warnings, skipped } = this.filterCompatibleStyles(
      extractedStyles,
      targetComponent,
      propertyScope
    );

    // Step 4: Build PropertyChange object
    const propertyChanges = this.buildPropertyChanges(compatible, targetComponent);

    // Return empty result if no changes
    if (
      Object.keys(propertyChanges.style || {}).length === 0 &&
      Object.keys(propertyChanges.properties || {}).length === 0 &&
      !propertyChanges.size
    ) {
      return {
        success: false,
        sourceComponent,
        warnings,
        skippedProperties: skipped,
        error: `No compatible styles found to clone from "${sourceComponent.name || sourceComponent.type}"`
      };
    }

    return {
      success: true,
      clonedStyles: propertyChanges,
      sourceComponent,
      warnings,
      skippedProperties: skipped
    };
  }

  // ============================================================================
  // Resolution Methods
  // ============================================================================

  /**
   * Resolve a source reference to a component
   */
  private resolveSourceComponent(
    sourceRef: string,
    context?: StyleCloneRequest['context']
  ): {
    success: boolean;
    component?: Component;
    error?: string;
    needsClarification?: boolean;
    matches?: Array<{
      id: string;
      name: string;
      type: string;
      matchReason: string;
      confidence: number;
    }>;
  } {
    // Use ComponentReferenceResolver for semantic resolution
    const result = componentReferenceResolver.resolveSemanticReference(sourceRef, {
      selectedComponents: context?.selectedComponents,
      templateInstanceId: context?.templateInstanceId,
      pageId: context?.pageId
    });

    if (result.success && result.primaryMatch) {
      // Get the actual component from store
      const { components } = useEditorStore.getState();
      const component = components.find(c => c.id === result.primaryMatch!.resolvedComponentId);

      if (component) {
        return { success: true, component };
      }
    }

    // If semantic resolution failed, try legacy resolution
    const legacyResult = componentReferenceResolver.resolveReference(sourceRef, {
      selectedComponents: context?.selectedComponents,
      templateInstanceId: context?.templateInstanceId
    });

    if (legacyResult) {
      return { success: true, component: legacyResult.component };
    }

    // If needs clarification, return the matches for the clarification dialog
    if (result.needsClarification && result.allMatches && result.allMatches.length > 0) {
      const { components } = useEditorStore.getState();
      const matches = result.allMatches.map(match => {
        const component = components.find(c => c.id === match.id);
        return {
          id: match.id,
          name: component?.name || match.name || match.id,
          type: String(component?.type || match.type || 'unknown'),
          matchReason: match.matchReason || 'Similar match',
          confidence: match.confidence
        };
      });

      return {
        success: false,
        needsClarification: true,
        matches,
        error: `Multiple components match "${sourceRef}". Please select one.`
      };
    }

    return {
      success: false,
      error: `Could not find component matching "${sourceRef}"`
    };
  }

  // ============================================================================
  // Public Helper Methods (for clarification flow)
  // ============================================================================

  /**
   * Public method to extract styles from a component
   * Used when clarification resolves the source component directly
   */
  extractStylesForClone(component: Component, scope: StyleCloneScope): ExtractedStyles {
    return this.extractStyles(component, scope);
  }

  /**
   * Public method to filter styles and build property changes
   * Used when clarification resolves the source component directly
   */
  filterAndBuildChanges(
    extractedStyles: ExtractedStyles,
    targetComponent: Component,
    scope: StyleCloneScope
  ): { propertyChanges: PropertyChange | null; warnings: string[]; skipped: string[] } {
    const { compatible, warnings, skipped } = this.filterCompatibleStyles(
      extractedStyles,
      targetComponent,
      scope
    );

    const propertyChanges = this.buildPropertyChanges(compatible, targetComponent);

    // Check if there are any actual changes
    if (
      Object.keys(propertyChanges.style || {}).length === 0 &&
      Object.keys(propertyChanges.properties || {}).length === 0 &&
      !propertyChanges.size
    ) {
      return { propertyChanges: null, warnings, skipped };
    }

    return { propertyChanges, warnings, skipped };
  }

  // ============================================================================
  // Style Extraction Methods
  // ============================================================================

  /**
   * Extract styles from a component based on scope
   * Handles the style location hierarchy:
   * 1. responsivePosition.breakpoints.desktop.style
   * 2. component.style
   * 3. component.properties (for component-specific props)
   */
  private extractStyles(component: Component, scope: StyleCloneScope): ExtractedStyles {
    const scopeProps = STYLE_SCOPES[scope];
    const extractedStyle: Record<string, unknown> = {};
    const extractedProperties: Record<string, unknown> = {};

    // Priority order for style extraction
    const styleSources = [
      component.responsivePosition?.breakpoints?.desktop?.style || {},
      component.style || {},
      component.properties?.style || {}
    ];

    // Merge all style sources (later sources override earlier ones)
    const mergedStyles: Record<string, unknown> = {};
    for (const source of styleSources) {
      Object.assign(mergedStyles, source);
    }

    // Extract style properties matching scope
    for (const [key, value] of Object.entries(mergedStyles)) {
      if (value !== undefined && value !== null && value !== '') {
        if (scope === 'all' || scopeProps.includes(key)) {
          extractedStyle[key] = value;
        }
      }
    }

    // Extract relevant properties from component.properties
    const componentProps = component.properties || {};
    for (const [key, value] of Object.entries(componentProps)) {
      if (value !== undefined && value !== null && value !== '') {
        // Check if this property belongs to the requested scope
        if (scopeProps.includes(key)) {
          extractedProperties[key] = value;
        }
      }
    }

    // Include size for 'all' scope
    const result: ExtractedStyles = {
      style: extractedStyle,
      properties: extractedProperties
    };

    // Only include size for 'all' or 'layout' scope
    if (scope === 'all' || scope === 'layout') {
      if (component.size) {
        result.size = {
          width: component.size.width,
          height: component.size.height
        };
      }
    }

    return result;
  }

  // ============================================================================
  // Compatibility Filtering Methods
  // ============================================================================

  /**
   * Filter extracted styles for compatibility with target component
   */
  private filterCompatibleStyles(
    extracted: ExtractedStyles,
    targetComponent: Component,
    scope: StyleCloneScope
  ): {
    compatible: ExtractedStyles;
    warnings: string[];
    skipped: string[];
  } {
    const compatible: ExtractedStyles = {
      style: {},
      properties: {}
    };
    const warnings: string[] = [];
    const skipped: string[] = [];

    const targetType = targetComponent.type;

    // Filter style properties
    for (const [key, value] of Object.entries(extracted.style)) {
      const isCompatible = this.isPropertyCompatible(key, targetType);
      if (isCompatible) {
        compatible.style[key] = value;
      } else {
        skipped.push(key);
        // Only warn for 'all' scope, as scope-specific cloning is intentional
        if (scope === 'all') {
          warnings.push(`Property "${key}" is not applicable to ${targetType} components`);
        }
      }
    }

    // Filter component properties
    for (const [key, value] of Object.entries(extracted.properties)) {
      const isCompatible = this.isPropertyCompatible(key, targetType);
      if (isCompatible) {
        compatible.properties[key] = value;
      } else {
        skipped.push(key);
        if (scope === 'all') {
          warnings.push(`Property "${key}" is not applicable to ${targetType} components`);
        }
      }
    }

    // Include size if extracted and applicable
    if (extracted.size) {
      compatible.size = extracted.size;
    }

    return { compatible, warnings, skipped };
  }

  /**
   * Check if a property is compatible with a component type
   */
  private isPropertyCompatible(propertyName: string, targetType: ComponentType | string): boolean {
    // Check if property has type restrictions
    const restrictions = COMPONENT_SPECIFIC_PROPERTIES[propertyName];

    if (restrictions) {
      // Property has restrictions - check if target type is allowed
      return restrictions.includes(targetType as ComponentType) ||
             restrictions.some(t => t.toString() === targetType.toString());
    }

    // No restrictions - property is universally applicable
    return true;
  }

  // ============================================================================
  // Property Change Building Methods
  // ============================================================================

  /**
   * Build a PropertyChange object from extracted and filtered styles
   */
  private buildPropertyChanges(
    compatible: ExtractedStyles,
    targetComponent: Component
  ): PropertyChange {
    const changes: PropertyChange = {};

    // Add style changes
    if (Object.keys(compatible.style).length > 0) {
      changes.style = { ...compatible.style };
    }

    // Add property changes
    if (Object.keys(compatible.properties).length > 0) {
      changes.properties = { ...compatible.properties };
    }

    // Add size changes if present
    if (compatible.size) {
      changes.size = { ...compatible.size };
    }

    // Set target component ID for proper application
    changes.targetComponentId = targetComponent.id;

    return changes;
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Get a human-readable description of what was cloned
   */
  getCloneDescription(result: StyleCloneResult, scope: StyleCloneScope): string {
    if (!result.success || !result.clonedStyles) {
      return result.error || 'Failed to clone styles';
    }

    const sourceName = result.sourceComponent?.name || result.sourceComponent?.type || 'component';
    const styleCount = Object.keys(result.clonedStyles.style || {}).length;
    const propCount = Object.keys(result.clonedStyles.properties || {}).length;
    const hasSize = !!result.clonedStyles.size;

    const parts: string[] = [];
    if (styleCount > 0) parts.push(`${styleCount} style properties`);
    if (propCount > 0) parts.push(`${propCount} component properties`);
    if (hasSize) parts.push('size');

    const scopeLabel = scope === 'all' ? 'all styles' : scope;

    return `Cloned ${scopeLabel} from "${sourceName}": ${parts.join(', ')}`;
  }

  /**
   * Get suggestions for style clone commands
   */
  getSuggestions(components: Component[]): string[] {
    const suggestions: string[] = [];

    // Get unique component names/types for suggestions
    const componentLabels = components
      .map(c => c.name || c.type)
      .filter((label, index, arr) => arr.indexOf(label) === index)
      .slice(0, 5);

    for (const label of componentLabels) {
      suggestions.push(`copy the style from ${label}`);
    }

    // Add generic suggestions
    suggestions.push(
      "match the header's colors",
      "use the same font as the title",
      "copy spacing from the card",
      "look like the button above"
    );

    return suggestions.slice(0, 8);
  }
}

// Export singleton instance
export const styleCloneService = StyleCloneService.getInstance();
